import { dataManagementDialogView } from "./view.ts";
import { createDialogOperations } from "../../ui/dialog-operations.ts";
import { bindPasswordVisibility } from "../../ui/password-visibility.ts";
import type { ProgressReporter } from "../../ui/operation-progress.ts";

export interface DataManagementCapabilities {
  hasActiveSpace: boolean;
  canEdit: boolean;
  icon(name: string): string;
  toast(message: string, error?: boolean): void;
  exportJson(progress?: ProgressReporter): Promise<string>;
  exportCsv(progress?: ProgressReporter): Promise<string>;
  exportMarkdown?(progress?: ProgressReporter): Promise<string>;
  importFile(file: File, progress?: ProgressReporter): Promise<string | null>;
  createBackup(password: string, progress?: ProgressReporter): Promise<string>;
  restoreBackup(file: File, password: string, progress?: ProgressReporter): Promise<string | null>;
}

export function openDataManagementDialog(capabilities: DataManagementCapabilities): void {
  const dialog = document.createElement("dialog");
  dialog.className = "name-dialog data-dialog";
  const operations = createDialogOperations(dialog, capabilities.toast);
  operations.render(dataManagementDialogView({ ...capabilities, supportsMarkdown: !!capabilities.exportMarkdown }));
  bindPasswordVisibility(dialog);
  const opener = document.activeElement as HTMLElement | null;
  dialog.querySelectorAll<HTMLButtonElement>("[data-data-panel-open]").forEach(button => button.addEventListener("click", () => {
    dialog.querySelectorAll<HTMLElement>("[data-data-panel]").forEach(panel => { panel.hidden = panel.dataset.dataPanel !== button.dataset.dataPanelOpen; });
    dialog.querySelectorAll<HTMLElement>("[data-data-panel-open]").forEach(item => item.setAttribute("aria-pressed", String(item === button)));
    const status = dialog.querySelector<HTMLElement>("[data-operation-status]"); if (status) status.textContent = "";
  }));
  const run = (operation: (progress: ProgressReporter) => Promise<string | null>, close = false) => operations.run(async () => {
    const message = await operation(operations.progress);
    if (!message) return;
    if (close) dialog.close();
    operations.toast(message);
  });
  dialog.querySelector("[data-data-close]")!.addEventListener("click", () => dialog.close());
  const format = dialog.querySelector<HTMLSelectElement>("[data-export-format]")!;
  format.addEventListener("change", () => { dialog.querySelector("[data-export-explanation]")!.textContent = format.value === "json" ? "Includes supported task data, attachments and history. Requires a connection." : format.value === "csv" ? "Spreadsheet of tasks. Attachments and history are omitted. Requires a connection." : "Readable task and Reference text. Attachments and history are omitted."; });
  dialog.querySelector("[data-export-selected]")!.addEventListener("click", () => { const operation = format.value === "csv" ? capabilities.exportCsv : format.value === "markdown" ? capabilities.exportMarkdown : capabilities.exportJson; if (operation) void run(operation); });
  dialog.querySelector<HTMLInputElement>("[data-import-file]")!.addEventListener("change", event => {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (file) void run(progress => capabilities.importFile(file, progress), true).finally(() => { input.value = ""; });
  });
  dialog.querySelector("[data-create-backup]")!.addEventListener("click", () => {
    const input = dialog.querySelector<HTMLInputElement>("[data-backup-password]")!;
    if (!input.reportValidity()) return;
    void run(progress => capabilities.createBackup(input.value, progress));
  });
  const file = dialog.querySelector<HTMLInputElement>("[data-restore-backup]")!;
  const restore = dialog.querySelector<HTMLButtonElement>("[data-restore-selected]")!;
  file.addEventListener("change", () => { restore.disabled = !file.files?.length; });
  restore.addEventListener("click", () => {
    const input = dialog.querySelector<HTMLInputElement>("[data-restore-password]")!;
    if (!input.reportValidity() || !file.files?.[0]) return;
    const selected = file.files[0]; const password = input.value;
    void run(progress => capabilities.restoreBackup(selected, password, progress), true);
  });
  document.body.append(dialog);
  dialog.addEventListener("close", () => { dialog.remove(); if (opener?.isConnected) opener.focus({ preventScroll: true }); });
  dialog.showModal();
}
